// AI 설정 · AI 호출 로그 공통 상수
// 백엔드는 코드만 내려주고 표시명은 화면이 갖습니다.
// 근거: docs/AI설정_관리자화면_가이드.md §2-1, §2-2, §2-3, §3-1

/** AI 를 쓰는 기능 3종. 서버의 items 는 항상 이 3개이고 늘거나 줄지 않습니다(§2-1). */
export const FEATURES = [
  {
    code: 'generate_answer',
    label: '질문 답변',
    desc: '학습자 질문에 AI 가 답하는 기능 (사용자가 기다리는 기능)',
    icon: 'forum',
  },
  {
    code: 'pip_performance',
    label: 'PIP 수행 채점',
    desc: '역할 수행 녹음을 AI 가 채점 (5분 주기 배치)',
    icon: 'grading',
  },
  {
    code: 'report_summary',
    label: '학생 리포트 요약',
    desc: '강사용 학생 리포트의 피드백 종합',
    icon: 'summarize',
  },
]

/**
 * 기능 코드 → 표시명.
 * AI 호출 로그에는 연결 테스트(`profile_test`)도 feature 로 섞여 옵니다(§3-1).
 */
export const featureLabel = (code) => {
  if (code === 'profile_test') return '연결 테스트'
  return FEATURES.find((f) => f.code === code)?.label || code || '—'
}

/** 로그 필터용 기능 목록 — 3종 + 연결 테스트 */
export const LOG_FEATURES = [
  ...FEATURES.map((f) => ({ code: f.code, label: f.label })),
  { code: 'profile_test', label: '연결 테스트' },
]

/**
 * 프로필 종류별 표시명·주소 형태·쓸 수 있는 키 이름(§2-2).
 * 키는 자기 종류의 프로필에만 쓸 수 있어(다른 종류를 고르면 422),
 * 종류를 고르면 키 드롭다운을 여기 값 하나 + "없음" 으로 좁힙니다.
 */
export const PROVIDER_TYPES = {
  vllm: {
    label: '사내 GPU 서버 (vLLM)',
    urlHint: 'http://서버:포트',
    urlPlaceholder: 'http://10.0.0.10:8003',
    keyEnv: 'VLLM_API_KEY',
    cloudLocked: false,
  },
  azure_openai: {
    label: 'MS Azure OpenAI',
    urlHint:
      'https 이고 호스트가 Azure 도메인으로 끝나야 저장됩니다. 운영에서 쓰는 형태는 ' +
      'https://<리소스>.cognitiveservices.azure.com 입니다.',
    urlPlaceholder: 'https://my-resource.cognitiveservices.azure.com',
    keyEnv: 'AZURE_OPENAI_CHAT_API_KEY',
    cloudLocked: true,
  },
  openai_compat: {
    label: 'OpenAI 호환',
    urlHint: 'https://…/v1 (/v1 까지 포함)',
    urlPlaceholder: 'https://api.example.com/v1',
    keyEnv: 'OPENAI_COMPAT_API_KEY',
    cloudLocked: false,
  },
  genesis: {
    label: 'Genesis Lab',
    urlHint: 'http://서버:포트',
    urlPlaceholder: 'http://10.0.0.20:9000',
    keyEnv: 'GENESIS_LAB_AI_API_KEY05',
    cloudLocked: true,
  },
}

export const providerLabel = (type) => PROVIDER_TYPES[type]?.label || type || '—'

/**
 * `azure_openai` 프로필의 `base_url` 로 허용되는 호스트 접미사.
 * 근거: docs/AI설정_관리자화면_가이드.md §2-5
 *      (서버 구현 `core/ai_service/config_store.py` `is_azure_endpoint`)
 *
 * Azure 키는 결제 수단이 붙어 있어, 주소를 바꿔 키를 다른 서버로 빼돌리는 것을 막는 장치입니다.
 * 목록에 없는 도메인(예: 사내 APIM 게이트웨이)은 서버가 422 로 거부하므로,
 * 쓰게 되면 백엔드가 서버 목록에 추가한 뒤 이 상수도 같이 풀어야 합니다.
 */
export const AZURE_HOST_SUFFIXES = [
  '.openai.azure.com',            // Azure OpenAI 전용 리소스
  '.cognitiveservices.azure.com', // Azure AI Services 다중 서비스 리소스 — 운영이 이 형태입니다
  '.services.ai.azure.com',       // Azure AI Foundry 프로젝트
  '.openai.azure.us',             // Azure Government
  '.openai.azure.cn',             // Azure China
]

/**
 * 주소를 파싱해 http(s) 일 때만 URL 객체를 돌려줍니다. 깨진 주소·다른 스킴은 null.
 * 정규식으로 앞부분만 보면 `https://[abc` 같은 값이 화면을 통과하고 서버에서 422 가 납니다.
 */
export const parseHttpUrl = (value) => {
  let u
  try {
    u = new URL(String(value || '').trim())
  } catch {
    return null
  }
  return u.protocol === 'http:' || u.protocol === 'https:' ? u : null
}

/**
 * 서버의 Azure 주소 판정을 그대로 옮긴 것입니다(규칙: docs/AI설정_관리자화면_가이드.md §2-5).
 * **문자열 포함으로 검사하면 안 됩니다** — 운영 주소(`.cognitiveservices.azure.com`)를 막으면서
 * `https://evil.com/?x=.openai.azure.com` 같은 위장은 통과시킵니다.
 * 호스트 접미사만 보고 경로·쿼리·포트는 보지 않습니다.
 */
export const isAzureEndpoint = (baseUrl) => {
  const u = parseHttpUrl(baseUrl)
  if (!u || u.protocol !== 'https:') return false
  const host = u.hostname.toLowerCase()
  return AZURE_HOST_SUFFIXES.some((s) => host.endsWith(s))
}

/**
 * `azure_openai`·`genesis` 는 서버가 `is_cloud` 를 항상 true 로 강제합니다.
 * 화면에서는 토글을 잠가 사용자가 422 를 보지 않게 합니다(§2-2).
 */
export const isCloudLocked = (type) => PROVIDER_TYPES[type]?.cloudLocked === true

/** `options` 에 넣을 수 있는 키는 3개뿐입니다. 다른 키를 보내면 422(§2-2). */
export const MAX_TOKENS_PARAMS = ['max_tokens', 'max_completion_tokens']

/**
 * 연결 테스트·호출 로그의 `error_type` 을 관리자가 읽을 수 있는 문구로 풉니다(§2-3).
 * 뒤 4개(`circuit_open`~`profile_missing`)는 호출이 나가지 않고 끝난 경우로,
 * 로그에만 나옵니다(§3-1).
 */
export const ERROR_TYPE_LABELS = {
  connect: '서버에 연결할 수 없습니다 (주소가 틀렸거나 서버가 꺼져 있음)',
  timeout: '응답 시간 초과',
  auth: '인증 실패 (API 키가 없거나 틀림 — API 키 등록 여부 확인)',
  not_found: '모델(deployment) 이름이 틀렸거나 주소 경로가 틀림',
  rate_limit: '호출 한도 초과 (잠시 후 다시)',
  http_5xx: '상대 서버 내부 오류',
  bad_request: '상대 서버가 요청을 거부',
  config: '설정이 덜 채워짐',
  empty: '응답이 비었습니다',
  bad_format: '응답 형식이 다릅니다',
  unknown: '알 수 없는 오류',
  circuit_open: '연속 실패로 자동 차단된 상태라 호출하지 않음',
  inactive: '지정된 프로필이 비활성',
  cloud_blocked: '과금 차단 기능인데 과금 프로필이 지정돼 있어 건너뜀',
  profile_missing: '지정된 프로필이 없음',
}

export const errorTypeLabel = (type) => (type ? ERROR_TYPE_LABELS[type] || type : '')

/** 1순위·예비·연결 테스트 역할 표시(§2-4, §3-1) */
export const ROLE_LABELS = {
  primary: '1순위',
  fallback: '예비',
  test: '연결 테스트',
}

export const roleLabel = (role) => ROLE_LABELS[role] || role || '—'

/**
 * 설정 DB 를 읽지 못한 동안의 호출에 붙는 프로필 코드.
 * 프로필 목록에는 없는 코드라 "비상 기본값" 배지로 따로 알립니다(§3-1).
 */
export const BOOTSTRAP_PROFILE = 'bootstrap_vllm'

/** 토큰 수는 null 일 수 있습니다(Genesis Lab 은 토큰 수를 알려 주지 않음). 0 과 구분해 "—" 로 씁니다(§3-1). */
export const fmtTokens = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString())

/** 소요 시간. 호출이 나가지 않고 끝난 행은 0 으로 옵니다. */
export const fmtLatency = (ms) =>
  ms === null || ms === undefined ? '—' : `${Number(ms).toLocaleString()}ms`
